/**
 * MobileContractsTopMenu
 *
 * Mobile-optimized top navigation for ContractsView.
 * Replaces the desktop ContractsTopMenu when isMobile === 2.
 * Uses MUI Tabs for view switching (Contrats / Missions / Suivis)
 * and MUI IconButton for the export action.
 */
import React from "react";
import {
  Box,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import Langs from "../../../app/lang/langs";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)";

const iconButtonSx = {
  minWidth: 44,
  minHeight: 44,
  borderRadius: "12px",
  color: "rgba(0,0,0,0.7)",
  transition: TRANSITION_SMOOTH,
  "&:hover": {
    backgroundColor: "rgba(255,144,21,0.08)",
    transform: "scale(1.06)",
    boxShadow: "0 2px 12px rgba(255,144,21,0.18)",
    color: "rgb(255,144,21)",
  },
  "&:active": {
    transform: "scale(0.96)",
    boxShadow: "0 1px 4px rgba(255,144,21,0.12)",
  },
};

/**
 * @param {Object} props
 * @param {Array}  props.viewsOptions - [{label, value}, ...]
 * @param {number} props.selectedView
 * @param {Function} props.onViewChange
 * @param {Function} props.onExportContracts
 * @param {Function} props.onExportMissions
 * @param {Function} props.onExportSuivisMissions
 */
export default function MobileContractsTopMenu({
  viewsOptions,
  selectedView,
  onViewChange,
  onExportContracts,
  onExportMissions,
  onExportSuivisMissions,
}) {
  const handleTabChange = (_event, newValue) => {
    onViewChange(newValue);
  };

  const handleExport = () => {
    if (selectedView == 0) onExportContracts?.();
    else if (selectedView == 1) onExportMissions?.();
    else if (selectedView == 2) onExportSuivisMissions?.();
  };

  const getExportTooltip = () => {
    if (selectedView == 0) return <Langs str="Export contrats" />;
    if (selectedView == 1) return <Langs str="Export missions" />;
    if (selectedView == 2) return <Langs str="Export suivis missions" />;
    return "Export";
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        bgcolor: "rgba(255,255,255,0.78)",
        backdropFilter: "blur(20px) saturate(1.4)",
        WebkitBackdropFilter: "blur(20px) saturate(1.4)",
        borderBottom: "1px solid rgba(0,40,128,0.06)",
        boxShadow:
          "0 1px 12px rgba(0,40,128,0.05), 0 0.5px 3px rgba(0,0,0,0.03)",
        px: 1.5,
        pt: 0.75,
        pb: 0,
        transition: TRANSITION_SMOOTH,
        position: "relative",
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: 0,
          left: "5%",
          right: "5%",
          height: "1px",
          background:
            "linear-gradient(90deg, transparent, rgba(255,144,21,0.15), transparent)",
        },
      }}
    >
      {/* Title + Export button row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: 52,
          px: 0.5,
        }}
      >
        <Box
          sx={{
            fontWeight: 700,
            fontSize: "1.15rem",
            pl: 0.5,
            letterSpacing: "-0.025em",
            color: "rgb(0,0,0)",
            transition: "color 0.25s ease, opacity 0.25s ease",
            lineHeight: 1.3,
          }}
        >
          {viewsOptions.find((o) => o.value == selectedView)?.label}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Tooltip title={getExportTooltip()} arrow>
            <IconButton
              onClick={handleExport}
              size="large"
              sx={iconButtonSx}
            >
              <FileDownloadOutlinedIcon sx={{ fontSize: "1.35rem" }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Tabs row for view switching */}
      <Tabs
        value={selectedView}
        onChange={handleTabChange}
        variant="fullWidth"
        centered
        sx={{
          minHeight: 42,
          "& .MuiTab-root": {
            minHeight: 42,
            minWidth: 0,
            px: 2,
            py: 0.75,
            fontSize: "0.82rem",
            fontWeight: 600,
            textTransform: "none",
            letterSpacing: "-0.01em",
            color: "rgba(255,144,21,0.55)",
            borderRadius: "10px 10px 0 0",
            transition: TRANSITION_SMOOTH,
            position: "relative",
            "&:hover": {
              backgroundColor: "rgba(255,144,21,0.06)",
              color: "rgba(255,144,21,0.8)",
            },
            "&.Mui-selected": {
              fontWeight: 700,
              color: "rgb(255,144,21)",
            },
          },
          "& .MuiTabs-indicator": {
            height: 3,
            borderRadius: "3px 3px 0 0",
            background: "linear-gradient(90deg, #FF8701, #FFC100)",
            boxShadow: "0 0 8px rgba(255,144,21,0.35)",
            transition: "all 350ms cubic-bezier(0.4, 0, 0.2, 1)",
          },
        }}
      >
        {viewsOptions.map((option) => (
          <Tab key={option.value} label={option.label} value={option.value} />
        ))}
      </Tabs>
    </Box>
  );
}
